import { getAssociatedTokenAddressSync } from "@solana/spl-token";
import { PublicKey, Transaction } from "@solana/web3.js";
import { useConnection } from "@solana/wallet-adapter-react";
import { useState } from "react";
import Button from "@axvdex/components/common/Button";
import { Sale } from "@axvdex/types/solana/outbid";
import { findTransferAuthority } from "@axvdex/hooks/solana/utils/DeriveOutbidPDA";
import { createCollectBidAssetsInstruction, errorFromCode } from "@axvdex/solana/outbidAPI/js/generated";
import { getSale } from "@axvdex/hooks/solana/useOutbid";
import { sendWithPhantomAndDebugTransaction } from "@axvdex/hooks/solana/utils/client";
import { useAppDispatch } from "@axvdex/state";
import { sendToast } from "@axvdex/state/wallet/walletSlice";
import { SolanaSaleOperatorControlsProps } from "../SolanaSaleOperatorControls";

function Claim({ outbidSale }: SolanaSaleOperatorControlsProps) {
  const { connection } = useConnection();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);

  return (
    <div style={{ marginTop: "16px" }}>
      <Button
        title={"Collect funds"}
        btnColor="gradient"
        onClick={async () => {
          try {
            const transaction = new Transaction();
            for (const saleAddr of Object.keys(outbidSale.salesState)) {
              const sale = await getSale(connection, new PublicKey(saleAddr));
              if (!sale) {
                throw new Error("Sale not found");
              } else if (!sale.config.isProcessEnded) {
                throw new Error("Please send the funds to the sale first. See 'End Process' tab");
              }
              const collectTx = createCollectBidInstruction(sale);
              transaction.add(collectTx);
            }
            // const signature = await sendWithPhantomAndDebugTransaction(transaction, connection, errorFromCode);
            const signature = await window.solanaClient.sendTransaction(transaction, connection);
            await connection.confirmTransaction(signature, "finalized");
            const msg = "Funds collected";
            dispatch(sendToast({ type: "tx-success", info: { msg, toastID: "" + new Date().getTime() } }));
          } catch (error: unknown) {
            console.error(error);
            const msg = error instanceof Error ? error.message : "An error occurred";
            dispatch(sendToast({ type: "tx-fail", info: { msg, toastID: "" + new Date().getTime() } }));
          } finally {
            setIsLoading(false);
          }
        }}
      >
        {isLoading ? "Executing ..." : "Claim"}
      </Button>
    </div>
  );
}

export default Claim;

// HELPERS ============================================================================================

function createCollectBidInstruction(sale: Sale) {
  const bidAsset = sale.config.bidAssets;
  const [transferAuthority] = findTransferAuthority(sale.saleAccount);
  const srcTokenAccount = getAssociatedTokenAddressSync(bidAsset[0].mint, transferAuthority, true);
  const destTokenAccount = getAssociatedTokenAddressSync(bidAsset[0].mint, window.solanaClient.publicKey);
  const collectTx = createCollectBidAssetsInstruction({
    saleAccount: sale.saleAccount,
    saleAuthority: window.solanaClient.publicKey,
    transferAuthority: transferAuthority,
    bidAssetDest: destTokenAccount,
    bidAssetAta: srcTokenAccount,
    bidAssetMint: bidAsset[0].mint,
  });
  // Add the remaining bid accoutn (IDL can't generate multiple bid assets)
  for (let i = 1; i < bidAsset.length; i++) {
    const mint = bidAsset[i].mint;
    const srcTokenAccount = getAssociatedTokenAddressSync(mint, transferAuthority, true);
    const destTokenAccount = getAssociatedTokenAddressSync(mint, window.solanaClient.publicKey);
    collectTx.keys.push({
      pubkey: destTokenAccount,
      isWritable: true,
      isSigner: false,
    });
    collectTx.keys.push({
      pubkey: srcTokenAccount, // SrcAccount
      isWritable: true,
      isSigner: false,
    });
    collectTx.keys.push({
      pubkey: mint,
      isWritable: false,
      isSigner: false,
    });
  }

  return collectTx;
}
